import React, { useState, useRef, useEffect } from "react";
import classes from './TrialBalance.module.css';
import { useReactToPrint } from "react-to-print";
import HeaderNav from '../../Components/Dahboard Header/HeaderNav/HeaderNav';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    BarChart2,
    Filter,
    CalendarClock,
    Mail,
    ArrowLeft,
    Printer,
    Download,
    Settings,
    Loader2
} from 'lucide-react';
import Footer from "../../Components/Footer/Footer";
import axios from "axios";
import Swal from "sweetalert2";
import { BASE_URL, withCompanyId } from '../../Components/API/Api';

const TrialBalance = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [rows, setRows] = useState([]);
    const [totals, setTotals] = useState({ debit: 0, credit: 0 });
    const [loading, setLoading] = useState(false);
    const [companyName, setCompanyName] = useState("Loading...");
    const [companyPhone, setCompanyPhone] = useState("");
    const [companyEmail, setCompanyEmail] = useState("");
    const [companyAddress, setCompanyAddress] = useState("");
    const [companyRc, setCompanyRc] = useState("");
    const [companyLogo, setCompanyLogo] = useState("");
    const [generatedDate, setGeneratedDate] = useState(new Date().toLocaleString());

    const reportRef = useRef();

    // Fetch company information on component mount
    useEffect(() => {
        const fetchCompanyInfo = async () => {
            try {
                const authToken = localStorage.getItem("authToken");
                const companyId = localStorage.getItem("company_id") || localStorage.getItem("activeCompanyId");

                if (!authToken || !companyId) {
                    console.warn("Missing auth token or company ID");
                    return;
                }

                const res = await axios.get(
                    `${BASE_URL}/v1/companies/get-info?company_id=${companyId}`,
                    {
                        headers: {
                            Authorization: `Bearer ${authToken}`,
                        },
                    }
                );

                if (res.data && res.data.data) {
                    const companyData = Array.isArray(res.data.data)
                        ? res.data.data.find(c => String(c.id) === String(companyId)) || res.data.data[0]
                        : res.data.data;

                    setCompanyName(companyData.name || "Company Name");
                    setCompanyEmail(companyData.email || "");
                    setCompanyPhone(companyData.phone_number || "");
                    setCompanyAddress(companyData.address || "");
                    setCompanyRc(companyData.vat_registration ? `RC No: ${companyData.vat_registration}` : "");
                    if (companyData.logo) {
                        setCompanyLogo(companyData.logo);
                    }
                }
            } catch (err) {
                console.error("Error fetching company info:", err);
            }
        };

        fetchCompanyInfo();
    }, []);

    useEffect(() => {
        setGeneratedDate(new Date().toLocaleString());
    }, [rows]);

    const pageMap = {
        '/dashboard': 'Dashboard',
        '/transactions': 'Transactions',
        '/accounts': 'Accounting',
        '/reports': 'Reports',
        '/banking': 'Banking',
        '/settings': 'Settings',
    };

    const handleClick = () => {
        navigate('/report/financial-statements');
    };

    useEffect(() => {
        const handleAfterPrint = () => {
            if (document?.documentElement?.dataset) {
                delete document.documentElement.dataset.printView;
            }
        };

        window.addEventListener("afterprint", handleAfterPrint);
        return () => window.removeEventListener("afterprint", handleAfterPrint);
    }, []);

    const printReportOnly = () => {
        const reportEl = reportRef.current;
        if (!reportEl) return;

        const reportClone = reportEl.cloneNode(true);
        const toolbars = reportClone.querySelectorAll('[class*="iconbarss"], [class*="iconbars"]');
        toolbars.forEach((el) => el.remove());

        const printWindow = window.open("", "_blank", "width=1000,height=800");
        if (!printWindow) return;

        printWindow.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
                <title>Trial Balance</title>
                <style>
                    @page { margin: 12mm; size: auto; }
                    html, body {
                        margin: 0;
                        padding: 0;
                        background: #ffffff;
                        font-family: "Segoe UI", Tahoma, sans-serif;
                        -webkit-print-color-adjust: exact;
                        print-color-adjust: exact;
                    }
                    #report {
                        width: 100%;
                        box-sizing: border-box;
                        margin: 0 auto;
                        background: #fff;
                        padding: 25px;
                        box-shadow: none;
                    }
                    #report table {
                        width: 100%;
                        border-collapse: collapse;
                    }
                    #report thead, #report thead tr, #report thead th {
                        background: #2c3e50 !important;
                        color: #fff !important;
                        -webkit-print-color-adjust: exact !important;
                        print-color-adjust: exact !important;
                    }
                    #report th, #report td {
                        border: 1px solid #ddd;
                        padding: 8px;
                    }
                </style>
            </head>
            <body>
                <div id="print-root"></div>
            </body>
            </html>
        `);
        printWindow.document.close();

        const mountPoint = printWindow.document.getElementById("print-root");
        if (mountPoint) {
            mountPoint.appendChild(reportClone);
        }

        printWindow.focus();
        setTimeout(() => {
            printWindow.print();
            printWindow.close();
        }, 250);
    };

    const handlePrinter = () => {
        printReportOnly();
    };
    const handlePrint = () => {
        printReportOnly();
    };

    // const handlePrint = useReactToPrint({
    //     contentRef: componentRef,
    //     documentTitle,
    //     copyStyles: true,
    //     pageStyle: `
    //       @page { margin: 12mm; size: auto; }
    //       html, body { height: auto !important; background: #ffffff !important; margin: 0; padding: 0; }
    //       body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    //       #invoice-print-root { display: block !important; visibility: visible !important; position: relative !important; width: 100% !important; min-height: 100vh !important; background: #fff !important; color: #333 !important; padding: 12mm !important; }
    //       #invoice-print-root * { visibility: visible !important; }
    //       #invoice-print-root > div:first-child { display: none !important; }
    //     `,
    //   });

    const handleRunReport = async (e) => {
        e.preventDefault();
        if (!startDate || !endDate) {
            Swal.fire({
                icon: "error",
                title: "Missing Dates",
                text: "Please select start and end dates",
            });
            return;
        }

        try {
            setLoading(true);
            const authToken = localStorage.getItem("authToken");
            const res = await axios.post(
                `${BASE_URL}/v1/reports/get-trial-balance`,
                withCompanyId({ start_date: startDate, end_date: endDate }),
                {
                    headers: {
                        Authorization: `Bearer ${authToken}`,
                    },
                }
            );

            if (res.data && res.data.data) {
                const { values, debit, credit } = res.data.data;
                setRows(values || []);
                setTotals({ debit, credit });
            }
        } catch (err) {
            console.error("Error fetching trial balance:", err);

            Swal.fire({
                icon: "error",
                title: "Error",
                imageWidth: 48,
                imageHeight: 48,
                customClass: {
                    title: classes.myTitle,
                    popup: classes.myText,
                    confirmButton: classes.myButton,
                },
                background: "#fef3f2",
                confirmButtonText: "Okay",
                confirmButtonColor: "#16a34a",
                showCloseButton: true,
            });
        } finally {
            setLoading(false);
        }
    };

    const exportTableToExcel = () => {
        let table = document.getElementById("tbTable").outerHTML;
        let url = 'data:application/vnd.ms-excel,' + encodeURIComponent(table);
        let link = document.createElement("a");
        link.href = url;
        link.download = `trial_balance_${endDate}.xls`;
        link.click();
    };

    const printInvoice = () => {
        try {
            handlePrint();
        } catch (err) {
            printReportOnly();
        }
    };

    return (
        <div>
            <HeaderNav activePage={pageMap[location.pathname]} />

            <div className={classes.Container}>
                <div className={classes.actions}>
                    <button className={classes.btns}>New Sales Invoice</button>
                    <button className={classes.btns}>New Purchase Invoice</button>
                    <button className={classes.btns}>All Invoices</button>
                    <button className={`${classes.btns} ${classes.greens}`}>Product & Services</button>
                </div>

                <button onClick={handleClick} className={classes.backButton}>
                    <ArrowLeft size={16} style={{ marginRight: '6px' }} />
                </button>

                <div className={classes.act}>
                    <form className={classes.form} onSubmit={handleRunReport}>
                        <div className={classes.group1}>
                            <label>Start Date:</label>
                            <input
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                className={classes.statusDropdown2}
                            />
                        </div>

                        <div className={classes.group1}>
                            <label>End Date:</label>
                            <input
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                className={classes.statusDropdown2}
                            />
                        </div>

                        <div className={classes.group1}>
                            <button type="submit" className={classes.greenBtn} disabled={loading}>
                                <BarChart2 size={16} style={{ marginRight: "6px" }} />
                                {loading ? "Loading..." : "Run Report"}
                            </button>
                        </div>
                    </form>

                    <div className={classes.iconbars}>
                        <div className={classes.iconbarr}>
                            <div className={classes.iconbar}>
                                <Filter size={16} style={{ marginRight: "6px" }} />
                                Filter
                            </div>
                            <div className={classes.iconbar}>
                                <CalendarClock size={16} style={{ marginRight: "6px" }} />
                                Schedule
                            </div>
                        </div>

                       
                    </div>

                </div>

                {/* New HTML Template - Trial Balance Report */}
                <div style={trialBalanceStyles.pageStyle}>
                    {/* <div style={trialBalanceStyles.buttonsStyle}>
                        <button onClick={exportTableToExcel} style={trialBalanceStyles.buttonStyle}>
                            Export to Excel
                        </button>
                        <button onClick={handlePrinter} style={trialBalanceStyles.buttonStyle}>
                            Export to PDF
                        </button>
                    </div> */}

                    <div style={trialBalanceStyles.containerStyle} id="report" ref={reportRef}>
                        {/* Top Bar with Logo and Company Details */}
                        <div style={trialBalanceStyles.topBarStyle}>
                            <div style={trialBalanceStyles.logoStyle}>
                                {companyLogo ? (
                                    <img
                                        src={companyLogo}
                                        alt="Company Logo"
                                        style={{
                                            width: "100%",
                                            height: "100%",
                                            objectFit: "contain",
                                        }}
                                    />
                                ) : (
                                    "LOGO"
                                )}
                            </div>

                            <div style={trialBalanceStyles.companyDetailsStyle}>
                                <h2 style={trialBalanceStyles.companyNameStyle}>{companyName}</h2>
                                <p style={trialBalanceStyles.companyInfoStyle}>{companyAddress}</p>
                                <p style={trialBalanceStyles.companyInfoStyle}>
                                    {companyRc && `${companyRc} | `}
                                    {companyEmail && `${companyEmail} | `}
                                    {companyPhone}
                                </p>
                            </div>

 <div className={classes.iconbarss}>
                            <div className={classes.iconbar}>
                                <Mail size={16} />
                            </div>
                            <button
                                type="button"
                                className={classes.iconbar}
                                onClick={printInvoice}
                                style={{
                                    background: "transparent",
                                    border: "none",
                                    padding: 0,
                                    cursor: "pointer",
                                }}
                            >
                                <Printer size={16} />
                            </button>
                            <div className={classes.iconbar}>
                                <Download size={16} />
                            </div>
                            <div className={classes.iconbar}>
                                <Settings size={16} />
                            </div>
                        </div>
                            <div style={{ width: '80px' }}></div>
                        </div>

                        {/* Report Title */}
                        <div style={trialBalanceStyles.reportTitleStyle}>TRIAL BALANCE</div>

                        {/* Meta Information */}
                        <div style={trialBalanceStyles.metaContainerStyle}>
                            <div style={trialBalanceStyles.metaStyle}>
                                <div><strong>Period:</strong> As at {endDate || "—"}</div>
                                <div><strong>Generated:</strong> <span id="date">{generatedDate}</span></div>
                            </div>

                            <div style={trialBalanceStyles.metaStyle}>
                                <div><strong>Prepared By:</strong> Accountant</div>
                                <div><strong>Currency:</strong> NGN (₦)</div>
                            </div>
                        </div>

                        {/* Trial Balance Table */}
                        <table id="tbTable" style={trialBalanceStyles.tableStyle}>
                            <thead style={trialBalanceStyles.theadStyle}>
                                <tr>
                                    <th style={trialBalanceStyles.thStyle}>Code</th>
                                    <th style={trialBalanceStyles.thStyle}>Account Name</th>
                                    <th style={{ ...trialBalanceStyles.thStyle, textAlign: 'right', paddingRight: '20px' }}>Debit (₦)</th>
                                    <th style={{ ...trialBalanceStyles.thStyle, textAlign: 'right', paddingRight: '20px' }}>Credit (₦)</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.length > 0 ? (
                                    rows.map((row, index) => (
                                        <tr key={index} style={index % 2 === 0 ? { backgroundColor: '#f9f9f9' } : {}}>
                                            <td style={trialBalanceStyles.tdStyle}>{row.gl_code}</td>
                                            <td style={trialBalanceStyles.tdStyle}>{row.gl_name}</td>
                                            <td style={{ ...trialBalanceStyles.tdStyle, textAlign: 'right', paddingRight: '20px' }}>
                                                {row.nDebit ? Number(row.nDebit).toLocaleString("en-NG", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "—"}
                                            </td>
                                            <td style={{ ...trialBalanceStyles.tdStyle, textAlign: 'right', paddingRight: '20px' }}>
                                                {row.nCredit ? Number(row.nCredit).toLocaleString("en-NG", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "—"}
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="4" style={{ textAlign: "center", padding: "20px", border: '1px solid #ddd' }}>
                                            {loading ? (
                                                <span style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "8px" }}>
                                                    <Loader2 className={classes.spinner} size={18} />
                                                    Loading...
                                                </span>
                                            ) : (
                                                "No data available"
                                            )}
                                        </td>
                                    </tr>
                                )}
                            </tbody>

                            {/* Totals Footer */}
                            <tfoot>
                                <tr style={trialBalanceStyles.totalsStyle}>
                                    <td style={trialBalanceStyles.tdStyle}></td>
                                    <td style={trialBalanceStyles.tdStyle}><strong>Total</strong></td>
                                    <td style={{ ...trialBalanceStyles.tdStyle, textAlign: 'right', paddingRight: '20px' }}>
                                        <strong style={{ color: totals.debit !== totals.credit ? "red" : "rgba(22, 163, 74, 1)" }}>
                                            {Number(totals.debit).toLocaleString("en-NG", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </strong>
                                    </td>
                                    <td style={{ ...trialBalanceStyles.tdStyle, textAlign: 'right', paddingRight: '20px' }}>
                                        <strong>{Number(totals.credit).toLocaleString("en-NG", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                                    </td>
                                </tr>
                            </tfoot>
                        </table>

                        {/* Footer with Signatures */}
                        <div style={trialBalanceStyles.footerStyle}>
                            <div style={trialBalanceStyles.signatureStyle}>
                                <div>Prepared By</div>
                            </div>

                            <div style={trialBalanceStyles.signatureStyle}>
                                <div>Approved By</div>
                            </div>
                        </div>

                        <div style={trialBalanceStyles.generatedByFooterStyle}>
                            <div>Generated by</div>
                            <div>Promix Accounting System</div>
                            <div>www.promixaccounting.com</div>
                        </div>
                    </div>
                </div>
            </div>
            <Footer />
        </div>
    );
};


const trialBalanceStyles = {
    pageStyle: {
        fontFamily: '"Segoe UI", Tahoma, sans-serif',
        background: '#f4f6f9',
        padding: '20px',
    },
    buttonsStyle: {
        marginBottom: '10px',
        textAlign: 'right',
    },
    buttonStyle: {
        padding: '8px 12px',
        marginLeft: '5px',
        border: 'none',
        cursor: 'pointer',
        background: '#2c3e50',
        color: '#fff',
        borderRadius: '4px',
        fontSize: '14px',
    },
    containerStyle: {
        width: '210mm',
        margin: 'auto',
        background: '#fff',
        padding: '25px',
        boxShadow: '0 0 10px rgba(0, 0, 0, 0.1)',
    },
    topBarStyle: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    logoStyle: {
        width: '80px',
        height: '80px',
        border: '1px solid #ccc',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '12px',
    },
    companyDetailsStyle: {
        textAlign: 'center',
        flex: 1,
    },
    companyNameStyle: {
        margin: 0,
        fontSize: '20px',
        fontWeight: 'bold',
    },
    companyInfoStyle: {
        margin: '2px 0',
        fontSize: '13px',
    },
    reportTitleStyle: {
        textAlign: 'center',
        marginTop: '10px',
        fontSize: '18px',
        fontWeight: 'bold',
        borderTop: '2px solid #000',
        borderBottom: '2px solid #000',
        padding: '5px',
    },
    metaContainerStyle: {
        borderBottom: '3px solid #2c3e50',
        paddingBottom: '10px',
    },
    metaStyle: {
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: '10px',
        fontSize: '13px',
    },
    tableStyle: {
        width: '100%',
        borderCollapse: 'collapse',
        marginTop: '15px',
        fontSize: '13px',

    },
    theadStyle: {
        background: '#2c3e50',
        color: '#fff',
    },
    thStyle: {
        border: '1px solid #ddd',
        padding: '8px',
    },
    tdStyle: {
        border: '1px solid #ddd',
        padding: '8px',
    },
    totalsStyle: {
        fontWeight: 'bold',
        background: '#eee',
    },
    footerStyle: {
        marginTop: '30px',
        display: 'flex',
        justifyContent: 'space-between',
        fontSize: '13px',
    },
    generatedByFooterStyle: {
        marginTop: '20px',
        textAlign: 'center',
        fontSize: '13px',
        fontWeight: 600,
        color: '#6b7280',
        lineHeight: 1.35,
    },
    signatureStyle: {
        textAlign: 'center',
        width: '30%',
        marginTop: '40px',
        borderTop: '1px solid #000',
    },
};

export default TrialBalance;
